/**
 * AuthClient - Centralized Firebase Authentication operations
 * 
 * Provides a unified interface for all authentication operations with:
 * - Anonymous sign-in with automatic retry logic
 * - Auth state subscription management
 * - User information access
 * - Consistent error handling and logging
 * 
 * All async operations are wrapped with retry logic to handle transient failures.
 */

import { signInAnonymously, onAuthStateChanged as firebaseOnAuthStateChanged } from 'firebase/auth';
import { auth } from '../utils/firebase.js';
import errorHandler from '../handlers/ErrorHandler.js';
import withRetry from '../utils/withRetry.js';

class AuthClient {
    
    /**
     * Sign in the user anonymously
     * Called automatically on app startup if no user is already signed in
     * 
     * @async
     * @returns {Promise<string>} User ID (uid) of the anonymous user
     * @throws {Error} If anonymous sign-in fails after all retries
     * @example
     * const userId = await AuthClient.signInAnonymously();
     */
    static signInAnonymously = withRetry(
        async () => {
            try {
                const userCredential = await signInAnonymously(auth);
                return userCredential.user.uid;
            } catch (error) {
                const errorMessage = `Anonymous sign-in failed: ${error.message}`;
                errorHandler.handle(error, { operation: 'signInAnonymously' }, 'error');
                throw error;
            }
        },
        'AuthClient.signInAnonymously'
    );

    /**
     * Subscribe to authentication state changes
     * Called whenever user logs in, logs out, or session state changes
     * 
     * @param {Function} callback - Called with user object when auth state changes
     *                              User is null if signed out, object if signed in
     * @returns {Function} Unsubscribe function - call to remove the listener
     * @example
     * const unsubscribe = AuthClient.onAuthStateChanged((user) => {
     *     if (user) {
     *         console.log('User logged in:', user.uid);
     *     } else {
     *         console.log('User logged out');
     *     }
     * });
     * 
     * // Later, stop listening
     * unsubscribe();
     */
    static onAuthStateChanged(callback) {
        try {
            // Validate parameters
            if (!callback || typeof callback !== 'function') {
                throw new Error('callback is required and must be a function');
            }

            return firebaseOnAuthStateChanged(auth, callback);
        } catch (error) {
            const errorMessage = `Failed to subscribe to auth state changes: ${error.message}`;
            errorHandler.log('ERROR', errorMessage, { originalError: error });
            throw error;
        }
    }

    /**
     * Get the currently authenticated user
     * Returns null if no user is signed in
     * 
     * @returns {Object|null} Current user object or null
     * @example
     * const user = AuthClient.getCurrentUser();
     * if (user) {
     *     console.log('Current user ID:', user.uid);
     *     console.log('Anonymous:', user.isAnonymous);
     * }
     */
    static getCurrentUser() {
        return auth.currentUser;
    }

    /**
     * Get the current user's ID (uid)
     * Returns null if no user is signed in
     * 
     * @returns {string|null} User ID (uid) or null if not signed in
     * @example
     * const userId = AuthClient.getCurrentUserId();
     * if (userId) {
     *     console.log('User ID:', userId);
     * }
     */
    static getCurrentUserId() {
        return auth.currentUser ? auth.currentUser.uid : null;
    }

    /**
     * Check if current user is anonymous
     * 
     * @returns {boolean} True if user is anonymous, false otherwise
     * @example
     * if (AuthClient.isAnonymous()) {
     *     console.log('User is anonymous');
     * }
     */
    static isAnonymous() {
        const user = auth.currentUser;
        return user ? user.isAnonymous : false;
    }

    /**
     * Sign out the current user
     * After sign out, auth will automatically attempt anonymous sign-in
     * 
     * @async
     * @returns {Promise<void>}
     * @throws {Error} If sign-out fails after all retries
     * @example
     * await AuthClient.signOut();
     */
    static signOut = withRetry(
        async () => {
            try {
                await auth.signOut();
            } catch (error) {
                const errorMessage = `Sign-out failed: ${error.message}`;
                errorHandler.handle(error, { operation: 'signOut' }, 'error');
                throw error;
            }
        },
        'AuthClient.signOut'
    );

    /**
     * Get the auth instance directly (use sparingly - prefer specific methods above)
     * 
     * @returns {Object} Firebase Auth instance
     */
    static getAuthInstance() {
        return auth;
    }
}

export default AuthClient;
